import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { hu } from 'date-fns/locale';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  ZoomIn,
  Phone,
  TouchApp,
  Share,
  PhotoCamera,
  EventNote,
  Timer,
  PhotoLibrary,
  HelpOutline,
  EmojiEvents,
  Warning,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  BarChart,
  Bar,
} from 'recharts';
import adminAnalyticsService from '../../services/adminAnalyticsService';
import categoryService from '../../services/categoryService';
import { COUNTIES } from '../../constants/counties';
import DateRangeSelector from '../../components/Stats/DateRangeSelector';
import FunnelDisplay from '../../components/Stats/FunnelDisplay';

const KPI_CONFIG = [
  { key: 'PROFILE_VIEW',       label: 'Profil megtekintés',       icon: Person,       color: '#2a5d32', hint: 'Profiloldal megnyitások száma.' },
  { key: 'QUICK_VIEW_OPEN',    label: 'Gyors megtekintés',        icon: ZoomIn,       color: '#5b8159', hint: 'Előnézet ablak megnyitások száma.' },
  { key: 'CONTACT_REVEAL',     label: 'Kapcsolat felfedés',       icon: Phone,        color: '#4caf50', hint: '„Kapcsolati adatok mutatása" kattintások száma.' },
  { key: 'CONTACT_LINK_CLICK', label: 'Kapcsolat kattintás',      icon: TouchApp,     color: '#ff9800', hint: 'Telefonszámra, email címre vagy helyszín linkre kattintások száma.' },
  { key: 'SOCIAL_CLICK',       label: 'Közösségi média',          icon: Share,        color: '#1877f2', hint: 'Facebook, Instagram, TikTok vagy weboldal ikonra kattintások száma.' },
  { key: 'GALLERY_SWIPE',      label: 'Galéria lapozás',          icon: PhotoCamera,  color: '#9c27b0', hint: 'Galéria/lightbox lapozások száma.' },
  { key: 'OFFER_REQUEST',      label: 'Ajánlatkérés',             icon: EventNote,    color: '#f44336', hint: 'Sikeres ajánlatkérési űrlap beküldések száma.' },
  { key: '_avg_time',          label: 'Átl. profil idő',          icon: Timer,        color: '#795548', hint: 'Átlagos profilon töltött idő.' },
  { key: '_gallery_rate',      label: 'Galéria elkötelezettség',  icon: PhotoLibrary, color: '#009688', hint: 'Galériát is megnéző látogatók aránya.' },
];

const CHART_COLORS = {
  PROFILE_VIEW: '#2a5d32',
  QUICK_VIEW_OPEN: '#5b8159',
  CONTACT_REVEAL: '#4caf50',
  CONTACT_LINK_CLICK: '#ff9800',
  SOCIAL_CLICK: '#1877f2',
  GALLERY_SWIPE: '#9c27b0',
  OFFER_REQUEST: '#f44336',
};

const EVENT_LABELS = {
  PROFILE_VIEW: 'Profil megtekintés',
  QUICK_VIEW_OPEN: 'Gyors megtekintés',
  CONTACT_REVEAL: 'Kapcsolat felfedés',
  CONTACT_LINK_CLICK: 'Kapcsolat kattintás',
  SOCIAL_CLICK: 'Közösségi média',
  GALLERY_SWIPE: 'Galéria lapozás',
  OFFER_REQUEST: 'Ajánlatkérés',
};

const formatSeconds = (s) => {
  if (!s || s === 0) return '–';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec} mp`;
  return `${m} perc ${sec > 0 ? sec + ' mp' : ''}`.trim();
};

const CardShell = ({ title, children, sx }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3, ...sx }}>
    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
      {title && <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>{title}</Typography>}
      {children}
    </CardContent>
  </Card>
);

const AdminStatsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedDays, setSelectedDays] = useState(30);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [isCustom, setIsCustom] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');

  const [providerOptions, setProviderOptions] = useState([]);
  const [providerSearchLoading, setProviderSearchLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const providerSearchTimer = useRef(null);

  const [summary, setSummary] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [countyData, setCountyData] = useState({ countyBreakdown: [], eventCityDemand: [] });
  const [marketplaceKpis, setMarketplaceKpis] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getDateRangeStrings = useCallback(() => {
    if (isCustom && customStart && customEnd) {
      return { startDate: format(customStart, 'yyyy-MM-dd'), endDate: format(customEnd, 'yyyy-MM-dd') };
    }
    const end = new Date();
    const start = subDays(end, selectedDays - 1);
    return { startDate: format(start, 'yyyy-MM-dd'), endDate: format(end, 'yyyy-MM-dd') };
  }, [selectedDays, isCustom, customStart, customEnd]);

  // Kategóriák betöltése egyszer
  useEffect(() => {
    categoryService.getCategories().then((res) => {
      if (res.success) setCategories(res.data.categories || []);
    }).catch(() => {});
  }, []);

  // Szolgáltató kereső (debounce)
  const handleProviderSearch = (search) => {
    setProviderSearchLoading(true);
    if (providerSearchTimer.current) clearTimeout(providerSearchTimer.current);
    providerSearchTimer.current = setTimeout(async () => {
      try {
        const res = await adminAnalyticsService.searchProviders({ search });
        if (res.success) setProviderOptions(res.data);
      } finally {
        setProviderSearchLoading(false);
      }
    }, 300);
  };

  // Áttekintés + idősor betöltése
  useEffect(() => {
    if (isCustom && (!customStart || !customEnd)) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { startDate, endDate } = getDateRangeStrings();
        const filters = {
          startDate,
          endDate,
          providerId: selectedProvider?.id,
          categoryId: selectedProvider ? undefined : selectedCategoryId,
          county: selectedProvider ? undefined : selectedCounty,
        };
        const [summaryRes, timeSeriesRes] = await Promise.all([
          adminAnalyticsService.getOverviewSummary(filters),
          adminAnalyticsService.getOverviewTimeSeries(filters),
        ]);
        if (summaryRes.success) setSummary(summaryRes.data);
        if (timeSeriesRes.success) setTimeSeries(timeSeriesRes.data);
      } catch (e) {
        setError('Nem sikerült betölteni a statisztikákat.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedDays, isCustom, customStart, customEnd, selectedProvider, selectedCategoryId, selectedCounty, getDateRangeStrings]);

  // Marketplace-szintű bontások (csak akkor, ha nincs konkrét szolgáltató kiválasztva)
  useEffect(() => {
    if (selectedProvider) return;
    if (isCustom && (!customStart || !customEnd)) return;

    const loadBreakdowns = async () => {
      try {
        const { startDate, endDate } = getDateRangeStrings();
        const [catRes, countyRes, kpiRes] = await Promise.all([
          adminAnalyticsService.getCategoryBreakdown({ startDate, endDate }),
          adminAnalyticsService.getCountyBreakdown({ startDate, endDate }),
          adminAnalyticsService.getMarketplaceKpis({ startDate, endDate }),
        ]);
        if (catRes.success) setCategoryBreakdown(catRes.data);
        if (countyRes.success) setCountyData(countyRes.data);
        if (kpiRes.success) setMarketplaceKpis(kpiRes.data);
      } catch (e) {
        // a fő összegzés hibakezelése már lefedi a felhasználói visszajelzést
      }
    };

    loadBreakdowns();
  }, [selectedDays, isCustom, customStart, customEnd, selectedProvider, getDateRangeStrings]);

  const chartData = useMemo(() => {
    if (!timeSeries?.events) return [];
    const byDate = {};
    timeSeries.events.forEach(({ date, eventType, count }) => {
      if (!byDate[date]) byDate[date] = { date };
      byDate[date][eventType] = count;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [timeSeries]);

  const formatXAxis = (dateStr) => {
    try {
      return format(new Date(dateStr + 'T12:00:00'), 'MM.dd.', { locale: hu });
    } catch {
      return dateStr;
    }
  };

  const hasChartData = chartData.length > 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 6 }}>
        <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 2 }}>
          <Container maxWidth="xl" disableGutters>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton onClick={() => navigate('/admin')} size="small">
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h5" fontWeight={700} fontFamily="'Playfair Display', serif">
                  Statisztikák és elemzés
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Marketplace teljesítmény, szolgáltató-összehasonlítás
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="xl" sx={{ pt: 3 }}>
          <DateRangeSelector
            selectedDays={selectedDays}
            setSelectedDays={setSelectedDays}
            isCustom={isCustom}
            setIsCustom={setIsCustom}
            customStart={customStart}
            setCustomStart={setCustomStart}
            customEnd={customEnd}
            setCustomEnd={setCustomEnd}
          />

          {/* Szűrők */}
          <CardShell>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Autocomplete
                sx={{ minWidth: 280, flex: '1 1 280px' }}
                size="small"
                options={providerOptions}
                value={selectedProvider}
                loading={providerSearchLoading}
                getOptionLabel={(opt) => opt.businessName || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(_, value) => setSelectedProvider(value)}
                onInputChange={(_, value) => handleProviderSearch(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Szolgáltató keresése (admin nézet)" placeholder="pl. Anna Esküvői Fotózás" />
                )}
              />
              {!selectedProvider && (
                <>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Kategória</InputLabel>
                    <Select
                      label="Kategória"
                      value={selectedCategoryId}
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                    >
                      <MenuItem value="">Összes kategória</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Megye</InputLabel>
                    <Select
                      label="Megye"
                      value={selectedCounty}
                      onChange={(e) => setSelectedCounty(e.target.value)}
                    >
                      <MenuItem value="">Összes megye</MenuItem>
                      {COUNTIES.map((c) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          </CardShell>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

          {/* Provider percentilis sáv */}
          {selectedProvider && summary?.categoryPercentile?.percentile && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              {selectedProvider.businessName}: profil megtekintés — top {summary.categoryPercentile.percentile}%
              {selectedProvider.category?.name ? ` a(z) ${selectedProvider.category.name} kategóriában` : ''}
              {' '}({summary.categoryPercentile.value.toLocaleString('hu-HU')} megtekintés, {summary.categoryPercentile.total} szolgáltató közül #{summary.categoryPercentile.rank}).
            </Alert>
          )}

          {/* KPI csík */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', overflowX: 'auto' }}>
              {KPI_CONFIG.map((kpi, idx) => {
                let value;
                if (kpi.key === '_avg_time') value = summary?.avgTimeSeconds;
                else if (kpi.key === '_gallery_rate') value = summary?.galleryEngagementRate;
                else value = summary?.totals?.[kpi.key];

                const KpiIcon = kpi.icon;
                const displayVal = () => {
                  if (loading) return '–';
                  if (kpi.key === '_avg_time') return formatSeconds(value);
                  if (kpi.key === '_gallery_rate') return value !== undefined ? `${value}%` : '–';
                  return (value ?? 0).toLocaleString('hu-HU');
                };

                return (
                  <Tooltip key={kpi.key} title={kpi.hint} placement="bottom" arrow>
                    <Box sx={{
                      flex: '1 1 0', minWidth: 105, px: { xs: 1.5, md: 2 }, py: 1.5,
                      borderRight: idx < KPI_CONFIG.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: 1, flexShrink: 0, bgcolor: kpi.color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <KpiIcon sx={{ color: kpi.color, fontSize: 14 }} />
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, fontSize: { xs: '0.82rem', md: '0.875rem' } }}>
                          {loading ? <CircularProgress size={12} /> : displayVal()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{kpi.label}</Typography>
                        <HelpOutline sx={{ fontSize: 11, color: 'text.disabled' }} />
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Card>

          {/* Idősor */}
          <CardShell title="Aktivitás időbeli alakulása">
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : !hasChartData ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>Ebben az időszakban nem volt aktivitás.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    {Object.entries(CHART_COLORS).map(([key, color]) => (
                      <linearGradient key={key} id={`admin_grad_${key}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={color} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <ReTooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }}
                    formatter={(value, name) => [value, EVENT_LABELS[name] || name]}
                  />
                  {Object.entries(CHART_COLORS).map(([key, color]) => (
                    <Area key={key} type="monotone" dataKey={key} name={key} stroke={color} strokeWidth={2} fill={`url(#admin_grad_${key})`} dot={false} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardShell>

          {selectedProvider && (
            <CardShell title="Konverziós tölcsér">
              <FunnelDisplay funnel={summary?.funnel} loading={loading} />
            </CardShell>
          )}

          {!selectedProvider && (
            <>
              {/* Kategória bontás */}
              <CardShell title="Kategóriánkénti bontás">
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Kategória</TableCell>
                        <TableCell align="right">Szolgáltatók</TableCell>
                        <TableCell align="right">Megtekintés</TableCell>
                        <TableCell align="right">Ajánlatkérés</TableCell>
                        <TableCell align="right">Konverzió</TableCell>
                        <TableCell align="right">Átl. értékelés</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryBreakdown
                        .slice()
                        .sort((a, b) => b.providerCount - a.providerCount)
                        .map((row) => (
                          <TableRow
                            key={row.categoryId}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setSelectedCategoryId(row.categoryId)}
                            selected={selectedCategoryId === row.categoryId}
                          >
                            <TableCell>{row.categoryName}</TableCell>
                            <TableCell align="right">{row.providerCount}</TableCell>
                            <TableCell align="right">{(row.events?.PROFILE_VIEW || 0).toLocaleString('hu-HU')}</TableCell>
                            <TableCell align="right">{(row.events?.OFFER_REQUEST || 0).toLocaleString('hu-HU')}</TableCell>
                            <TableCell align="right">{row.conversionRate}%</TableCell>
                            <TableCell align="right">{row.avgRating ?? '–'}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardShell>

              {/* Megyei lefedettség */}
              <CardShell title="Lefedettség megyénként (szolgáltató székhely szerint)">
                <ResponsiveContainer width="100%" height={Math.max(300, countyData.countyBreakdown.length * 22)}>
                  <BarChart
                    data={countyData.countyBreakdown}
                    layout="vertical"
                    margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="county" width={140} tick={{ fontSize: 11 }} />
                    <ReTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar
                      dataKey="providerCount"
                      name="Szolgáltatók"
                      radius={[0, 4, 4, 0]}
                      onClick={(d) => setSelectedCounty(d.county)}
                      cursor="pointer"
                    >
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <TableContainer component={Paper} elevation={0} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Megye</TableCell>
                        <TableCell align="right">Szolgáltatók</TableCell>
                        <TableCell align="right">Esemény</TableCell>
                        <TableCell align="right">Átl. értékelés</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {countyData.countyBreakdown.map((row) => (
                        <TableRow
                          key={row.county}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setSelectedCounty(row.county)}
                          selected={selectedCounty === row.county}
                        >
                          <TableCell>
                            {row.county}
                            {row.providerCount === 0 && (
                              <Chip label="nincs lefedettség" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell align="right">{row.providerCount}</TableCell>
                          <TableCell align="right">{row.eventCount.toLocaleString('hu-HU')}</TableCell>
                          <TableCell align="right">{row.avgRating ?? '–'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardShell>

              {/* Esküvői helyszín szerinti kereslet (eltér a szolgáltató székhelyétől) */}
              <CardShell title="Esküvői helyszín szerinti kereslet (ahol az esküvők zajlanak)">
                {countyData.eventCityDemand.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Nincs adat ebben az időszakban.</Typography>
                ) : (
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Helyszín (város)</TableCell>
                          <TableCell align="right">Foglalások</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {countyData.eventCityDemand.map((row) => (
                          <TableRow key={row.city}>
                            <TableCell>{row.city}</TableCell>
                            <TableCell align="right">{row.bookingCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardShell>

              {/* Marketplace KPI-k */}
              {marketplaceKpis && (
                <>
                  <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 3 }, flexWrap: 'wrap' }}>
                    <CardShell title="Jóváhagyásra váró szolgáltatók" sx={{ flex: '1 1 280px' }}>
                      <Typography variant="h4" fontWeight={700}>{marketplaceKpis.pendingBacklog.count}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Átlagos várakozás: {marketplaceKpis.pendingBacklog.avgDays} nap, max: {marketplaceKpis.pendingBacklog.maxDays} nap
                      </Typography>
                      {marketplaceKpis.pendingBacklog.maxDays > 14 && (
                        <Alert severity="warning" icon={<Warning />} sx={{ mt: 1.5, borderRadius: 2 }}>
                          Van olyan jelentkezés, ami {marketplaceKpis.pendingBacklog.maxDays} napja vár jóváhagyásra.
                        </Alert>
                      )}
                    </CardShell>

                    <CardShell title="Featured előfizetés bevétel (MRR)" sx={{ flex: '1 1 280px' }}>
                      <Typography variant="h4" fontWeight={700}>
                        {marketplaceKpis.featuredRevenue.monthlyRecurringRevenue.toLocaleString('hu-HU')} Ft
                      </Typography>
                      <Typography variant="body2" color="text.secondary">havi szintre vetítve, aktív előfizetésekből</Typography>
                      {Object.entries(marketplaceKpis.featuredRevenue.byTier).map(([tier, data]) => (
                        <Typography key={tier} variant="caption" display="block" color="text.secondary">
                          {tier}: {data.activeCount} aktív, {Math.round(data.monthlyRevenue).toLocaleString('hu-HU')} Ft/hó
                        </Typography>
                      ))}
                    </CardShell>

                    <CardShell title="Jutalék kifizetési státusz" sx={{ flex: '1 1 280px' }}>
                      {Object.entries(marketplaceKpis.commissionPayoutBreakdown).map(([status, amount]) => (
                        <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{status}</Typography>
                          <Typography variant="body2" fontWeight={600}>{amount.toLocaleString('hu-HU')} Ft</Typography>
                        </Box>
                      ))}
                    </CardShell>
                  </Box>

                  <CardShell title="Szolgáltató-növekedés (jóváhagyott, hetente)">
                    {marketplaceKpis.providerGrowth.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Nincs adat ebben az időszakban.</Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={marketplaceKpis.providerGrowth} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <ReTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                          <Bar dataKey="count" name="Új szolgáltató" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardShell>

                  <CardShell title="Engagement ranglista (top 10, profil megtekintés szerint)">
                    <TableContainer component={Paper} elevation={0}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Szolgáltató</TableCell>
                            <TableCell align="right">Megtekintés</TableCell>
                            <TableCell align="right">Ajánlatkérés</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {marketplaceKpis.engagementLeaderboard.map((row, i) => (
                            <TableRow key={row.providerId}>
                              <TableCell>
                                {i < 3 && <EmojiEvents sx={{ fontSize: 16, color: '#f4b400', verticalAlign: 'middle', mr: 0.5 }} />}
                                {row.businessName}
                              </TableCell>
                              <TableCell align="right">{row.views}</TableCell>
                              <TableCell align="right">{row.offerRequests}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardShell>

                  {marketplaceKpis.categoryGaps.length > 0 && (
                    <CardShell title="Kategória-lefedettségi rések (0 jóváhagyott szolgáltatóval)">
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {marketplaceKpis.categoryGaps.map((g) => (
                          <Chip key={g.categoryId} label={g.categoryName} color="warning" variant="outlined" />
                        ))}
                      </Box>
                    </CardShell>
                  )}
                </>
              )}
            </>
          )}
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminStatsPage;
